import React from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../common/Icons';
import NotificationJobsBanner from '../notifications/NotificationJobsBanner';
import PageTransition from '../common/PageTransition';
import PlatformDateTime from '../common/PlatformDateTime';
import { useAuth } from '../../contexts/AuthContext';
import { AppRole, defaultPathForRole } from '../../lib/roles';

interface RoleShellProps {
  title: string;
  subtitle: string;
  avatar: string;
  navigation: Array<{ to: string; label: string; icon: string }>;
  /** When set with `actionTo`, renders a top-bar link to that route. */
  actionLabel?: string;
  actionTo?: string;
}

export default function RoleShell({ title, subtitle, avatar, navigation, actionLabel, actionTo }: RoleShellProps) {
  const { user, signOut, setActiveRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = React.useState(() => window.localStorage.getItem('engage:sidebarCollapsed') === 'true');
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const role = user?.activeRole ?? user?.role ?? null;

  React.useEffect(() => {
    window.localStorage.setItem('engage:sidebarCollapsed', String(collapsed));
  }, [collapsed]);

  React.useEffect(() => {
    if (!mobileOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileOpen]);

  React.useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setMobileOpen(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const showOrgBranding = role === 'orgAdmin' || role === 'employee' || role === 'orgManager';
  const orgLogoUrl = showOrgBranding ? user?.orgLogoUrl?.trim() : null;

  const derivedTitle = React.useMemo(() => {
    if (showOrgBranding && user?.orgName && user.orgName.trim()) {
      return user.orgName.trim();
    }
    return title;
  }, [showOrgBranding, title, user?.orgName]);

  const derivedAvatar = React.useMemo(() => {
    const fullName = user?.fullName?.trim();
    if (fullName) {
      const initials = fullName
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase() ?? '')
        .join('');
      if (initials) return initials;
    }

    const emailInitial = user?.email?.trim()?.[0]?.toUpperCase();
    if (emailInitial) return emailInitial;
    return avatar;
  }, [avatar, user?.email, user?.fullName]);

  async function handleSignOut() {
    await signOut();
    navigate('/signin', { replace: true });
  }

  function handleRoleChange(role: AppRole) {
    setActiveRole(role);
    navigate(defaultPathForRole(role), { replace: true });
  }

  const disableSidebarCollapse = role === 'employee' && location.pathname === '/employee';
  const effectiveCollapsed = disableSidebarCollapse ? false : collapsed;
  const desktopWidthClass = effectiveCollapsed ? 'lg:w-16' : 'lg:w-60';
  const mobileWidthClass = 'w-64';
  const mobileTransformClass = mobileOpen ? 'translate-x-0' : '-translate-x-full';
  const showSidebarText = !effectiveCollapsed || mobileOpen;

  function closeMobile() {
    setMobileOpen(false);
  }

  return (
    <div className="app-shell flex min-h-screen">
      {/* Mobile overlay */}
      {mobileOpen ? (
        <button
          type="button"
          aria-label="Close menu"
          onClick={closeMobile}
          className="fixed inset-0 z-30 bg-black/40 lg:hidden"
        />
      ) : null}

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-0 z-40 flex h-[100dvh] max-h-[100dvh] min-h-0 flex-col overflow-x-visible overflow-y-hidden bg-[var(--e2s-primary)] text-white transition-transform duration-200 ${mobileWidthClass} ${mobileTransformClass} lg:sticky lg:h-screen lg:max-h-none lg:translate-x-0 lg:overflow-visible lg:flex lg:transition-all ${desktopWidthClass}`}
      >
        {!disableSidebarCollapse ? (
          <button
            type="button"
            onClick={() => setCollapsed((prev) => !prev)}
            aria-label={effectiveCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className="absolute right-0 top-1/2 z-20 hidden h-12 w-4 -translate-y-1/2 translate-x-1/2 items-center justify-center rounded-r-full rounded-l-md border border-white/15 bg-[var(--e2s-primary)] text-white/75 shadow-lg transition hover:text-white lg:flex"
          >
            <Icon
              name="chevron-left"
              size={14}
              color="currentColor"
              className={`transition-transform ${effectiveCollapsed ? 'rotate-180' : ''}`}
            />
          </button>
        ) : null}

        <div className="flex flex-shrink-0 items-center justify-between gap-3 border-b border-white/[0.08] px-4 py-5">
          <div className="flex items-center gap-3 overflow-hidden">
            {orgLogoUrl ? (
              <img src={orgLogoUrl} alt="" className="h-8 w-8 flex-shrink-0 rounded-lg object-contain bg-white/10" />
            ) : (
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-[var(--e2s-accent)] text-xs font-bold text-white">
                E
              </div>
            )}
            {showSidebarText && (
              <div className="overflow-hidden">
                <p className="truncate font-serif text-sm text-white">{derivedTitle}</p>
                <p className="truncate text-xs text-white/40">
                  {orgLogoUrl ? 'Powered by Engage to Succeed' : subtitle}
                </p>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={closeMobile}
            aria-label="Close menu"
            className="flex h-8 w-8 items-center justify-center rounded-md text-white/70 hover:bg-white/10 hover:text-white lg:hidden"
          >
            <Icon name="x" size={16} color="currentColor" />
          </button>
        </div>

        <nav className="min-h-0 flex-1 space-y-0.5 overflow-y-auto overscroll-contain px-2 py-4">
          {navigation.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to.split('/').filter(Boolean).length === 1}
              onClick={closeMobile}
              className={({ isActive }) =>
                `flex items-center rounded-lg px-3 py-2.5 text-sm font-normal transition ${effectiveCollapsed && !mobileOpen ? 'lg:justify-center' : 'gap-3'} ${
                  isActive ? 'bg-[var(--e2s-accent)] text-white' : 'text-white/75 hover:bg-white/[0.08]'
                }`
              }
            >
              <Icon name={item.icon} size={18} color="currentColor" className="flex-shrink-0 opacity-75" />
              {showSidebarText && <span>{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        <div className="flex flex-shrink-0 flex-col gap-1 border-t border-white/[0.08] bg-[var(--e2s-primary)] px-3 py-4">
          <button
            type="button"
            onClick={() => {
              void handleSignOut();
            }}
            className={`flex w-full items-center rounded-lg px-3 py-2.5 text-sm text-white/80 hover:bg-white/[0.12] hover:text-white ${effectiveCollapsed && !mobileOpen ? 'lg:justify-center' : 'gap-2'}`}
          >
            <Icon name="log-out" size={16} color="currentColor" className="flex-shrink-0 opacity-90" />
            {showSidebarText && <span>Sign out</span>}
          </button>
        </div>
      </aside>

      {/* Main area */}
      <div className="flex min-h-screen flex-1 flex-col overflow-auto">
        {/* Topbar */}
        <header className="flex flex-shrink-0 items-center justify-between border-b bg-white px-5 py-3" style={{ borderColor: 'var(--e2s-border)' }}>
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
              className="flex h-9 w-9 items-center justify-center rounded-md border lg:hidden"
              style={{ borderColor: 'var(--e2s-border)', color: 'var(--e2s-text)' }}
            >
              <Icon name="menu" size={18} color="currentColor" />
            </button>
            {orgLogoUrl ? (
              <img src={orgLogoUrl} alt="" className="hidden h-8 max-w-[140px] object-contain sm:block" />
            ) : (
              <img
                src="/assets/platform/logo-landscape.png"
                alt="Engage to Succeed"
                className="hidden h-8 object-contain sm:block"
              />
            )}
            <span className="text-base font-semibold" style={{ color: 'var(--e2s-text)' }}>{subtitle}</span>
          </div>
          <div className="flex items-center gap-3">
            <PlatformDateTime />
            {user && user.availableRoles.length > 1 ? (
              <select
                value={user.activeRole ?? user.role ?? ''}
                onChange={(event) => handleRoleChange(event.target.value as AppRole)}
                className="hidden rounded-xl border border-stone-200 bg-white px-3 py-2 text-xs font-semibold text-stone-600 outline-none sm:block"
              >
                {user.availableRoles.map((role) => (
                  <option key={role} value={role}>
                    {role === 'orgAdmin' ? 'Organisation' : role === 'orgManager' ? 'Team' : role === 'employee' ? 'Employee' : 'Platform'}
                  </option>
                ))}
              </select>
            ) : null}
            {actionLabel && actionTo ? (
              <Link to={actionTo} className="btn-primary hidden no-underline sm:inline-flex">
                {actionLabel}
              </Link>
            ) : null}
            <button
              type="button"
              onClick={() => {
                void handleSignOut();
              }}
              aria-label="Sign out"
              className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md border lg:hidden"
              style={{ borderColor: 'var(--e2s-border)', color: 'var(--e2s-muted)' }}
            >
              <Icon name="log-out" size={18} color="currentColor" />
            </button>
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white" style={{ background: 'var(--e2s-accent)' }}>
              {derivedAvatar}
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 p-6">
          {(role === 'orgAdmin' || role === 'superAdmin') && <NotificationJobsBanner />}
          <PageTransition>
            <Outlet />
          </PageTransition>
        </main>
      </div>
    </div>
  );
}
